/**
 * File: leadExportUtils.ts
 * Path: apps/admin/src/shared/utils/leadExportUtils.ts
 * Description: Spreadsheet exports of search results and of the pipeline.
 * Building the table is pure (and tested); writing the file is the only part
 * that touches the browser. Excel files are written by ./xlsxExport, loaded
 * only when one is asked for.
 */

import {
  formatPlaceType,
  LEAD_SOURCE_LABELS,
  LEAD_STAGE_LABELS,
  type Lead,
  type MapLeadCandidate,
} from "@idk/config";

export type Cell = string | number | null;

export interface ExportTable {
  headers: string[];
  rows: Cell[][];
}

export const candidatesTable = (candidates: MapLeadCandidate[]): ExportTable => ({
  headers: [
    "Business name",
    "Phone",
    "Website",
    "Rating",
    "Reviews",
    "Category",
    "Status on Google",
    "Address",
    "Google Maps link",
    "Place ID",
  ],
  rows: candidates.map((c) => [
    c.name,
    c.phone,
    c.website,
    c.rating,
    c.reviewCount,
    formatPlaceType(c.primaryType),
    c.businessStatus,
    c.address,
    c.googleMapsUri,
    c.placeId,
  ]),
});

export const leadsTable = (leads: Lead[]): ExportTable => ({
  headers: [
    "Business name",
    "Stage",
    "Contact person",
    "Phone",
    "Email",
    "Website",
    "Area",
    "Niche",
    "Source",
    "Estimated value (R)",
    "Next follow-up",
    "Do not contact",
    "Agreed to hear from us",
    "Rating",
    "Reviews",
    "Address",
    "Google Maps link",
  ],
  rows: leads.map((l) => [
    l.name,
    LEAD_STAGE_LABELS[l.stage],
    l.contactName,
    l.phone,
    l.email,
    l.website,
    l.area,
    l.niche,
    LEAD_SOURCE_LABELS[l.source],
    l.value,
    l.nextFollowUp,
    l.doNotContact ? "Yes" : "No",
    l.marketingConsent ? "Yes" : "No",
    l.place?.rating ?? null,
    l.place?.reviewCount ?? null,
    l.address,
    l.place?.googleMapsUri ?? "",
  ]),
});

/**
 * Text that Excel would run as a formula (a business named "=HYPERLINK(...)")
 * gets a leading apostrophe. Phone numbers like "+27 21 ..." are left alone.
 */
const neutraliseFormula = (value: string): string =>
  /^[=@\t\r]/.test(value) || /^[+-][^\d\s]/.test(value) ? `'${value}` : value;

const csvCell = (value: Cell): string => {
  if (value === null) return "";
  const text = typeof value === "number" ? String(value) : neutraliseFormula(value);
  return /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

/** CSV with a byte-order mark, so Excel reads accents and emoji correctly. */
export const toCsv = ({ headers, rows }: ExportTable): string =>
  "﻿" + [headers, ...rows].map((row) => row.map(csvCell).join(",")).join("\r\n");

/** "plumbers", "Grassy Park" → "plumbers-grassy-park-2026-10-01". */
export const exportFileName = (parts: string[], date: string): string => {
  const slug = parts
    .join(" ")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return `${slug || "leads"}-${date}`;
};

export const downloadBlob = (blob: Blob, filename: string): void => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  // Revoked on the next tick: some browsers start the download asynchronously.
  setTimeout(() => URL.revokeObjectURL(url), 0);
};

export const downloadCsv = (table: ExportTable, baseName: string): void =>
  downloadBlob(new Blob([toCsv(table)], { type: "text/csv;charset=utf-8" }), `${baseName}.csv`);

export type ExportFormat = "xlsx" | "csv";

/**
 * The one entry point the pages use. The Excel writer is imported on demand so
 * its zip code isn't in the bundle for people who never download.
 */
export const downloadExport = async (
  table: ExportTable,
  baseName: string,
  format: ExportFormat,
): Promise<void> => {
  if (format === "csv") return downloadCsv(table, baseName);
  const { downloadXlsx } = await import("./xlsxExportUtils");
  await downloadXlsx(table, baseName);
};
